/**
 * Runs a compiled policy over one set of inputs (plan §5.1, §5.4, §5.8).
 *
 * Three behaviours matter beyond "evaluate in dependency order":
 *
 *  - **Cost substitution.** An equation that carries a `costCategory` is evaluated to an *estimate*
 *    first, and then, if that category has recorded cost items, its resolved total replaces the value.
 *    Both numbers stay in the trace, so the cost tab can print "estimate → resolved" without a second
 *    calculation and the estimate never quietly disappears.
 *  - **Trade-in scope.** For a direct purchase the `TRADE_IN` equations are skipped, their outputs are
 *    `null` rather than zero, and reading a trade-in input is not a missing-input error — it is simply
 *    a question that was not asked.
 *  - **Rounding at the boundary.** Every equation's result is rounded to the scale of its declared unit
 *    before anything downstream reads it, so two runs of the same inputs are identical to the last
 *    decimal and a stored snapshot reproduces exactly.
 */
import { asEngineError, throwEngineError } from '../errors.js';
import { decimal, Money, roundToScale, scaleForUnit } from '../money.js';
import { isUnitName } from '../types.js';
import { evaluateNode, substituteExpression } from './evaluator.js';
const unitOf = (def) => (isUnitName(def.unit) ? def.unit : 'JOD');
/** Numbers are printed without exponent notation and without trailing zeros, the way an admin wrote them. */
const printValue = (value) => value.toDecimalPlaces(6).toString();
export const runPolicy = (policy, inputs, options = {}) => {
    const tradeIn = options.tradeIn ?? false;
    const costResolutions = options.costResolutions ?? {};
    const outputs = {};
    const trace = {};
    const errors = [];
    const evaluated = new Map();
    const evaluatedOrder = [];
    let provisional = false;
    const baseTrace = (entry) => ({
        id: entry.def.id,
        label: entry.def.label,
        unit: unitOf(entry.def),
        group: entry.def.group,
        scope: entry.def.scope,
        kind: entry.def.kind,
        expression: entry.parsed?.source ?? entry.def.expression,
        substituted: entry.parsed?.source ?? entry.def.expression,
        value: null,
    });
    const fail = (entry, error) => {
        const withId = { ...error, equationId: entry.def.id };
        outputs[entry.def.id] = null;
        trace[entry.def.id] = { ...baseTrace(entry), error: withId };
        errors.push(withId);
    };
    // Equations the compiler could not use at all — a syntax error or a cycle.
    for (const entry of policy.equations) {
        if (entry.error) {
            fail(entry, entry.error);
        }
    }
    for (const id of policy.order) {
        const entry = policy.byId[id];
        const parsed = entry.parsed;
        const unit = unitOf(entry.def);
        if (entry.def.scope === 'TRADE_IN' && !tradeIn) {
            outputs[id] = null;
            trace[id] = { ...baseTrace(entry), skipped: true };
            continue;
        }
        const substitutions = new Map();
        const resolve = (name, node) => {
            const upstream = evaluated.get(name);
            if (upstream !== undefined) {
                return upstream;
            }
            if (policy.byId[name]) {
                // A dependency that failed or was skipped: this equation cannot produce a figure either.
                return throwEngineError('MISSING_INPUT', `"${name}" has no value in this run`, { position: node.start, token: name });
            }
            const value = inputs[name];
            if (value === null || value === undefined || !Number.isFinite(value)) {
                return throwEngineError('MISSING_INPUT', `"${name}" was not supplied`, { position: node.start, token: name });
            }
            const asDecimal = decimal(value);
            substitutions.set(name, printValue(asDecimal));
            return asDecimal;
        };
        let value;
        try {
            value = evaluateNode(parsed.ast, resolve);
        }
        catch (error) {
            // Fill in whatever was resolved before the failure, so the substituted line still helps.
            const partial = substituteExpression(parsed.source, parsed.tokens, withUpstream(substitutions, parsed.references, evaluated));
            const failure = { ...asEngineError(error), equationId: id };
            outputs[id] = null;
            trace[id] = { ...baseTrace(entry), substituted: partial, error: failure };
            errors.push(failure);
            continue;
        }
        const rounded = new Money(roundToScale(value, scaleForUnit(unit)));
        const substituted = substituteExpression(parsed.source, parsed.tokens, withUpstream(substitutions, parsed.references, evaluated));
        let finalValue = rounded;
        let cost;
        if (entry.def.costCategory) {
            const resolution = costResolutions[entry.def.costCategory];
            const estimate = rounded.toNumber();
            if (resolution) {
                finalValue = new Money(roundToScale(resolution.amount, scaleForUnit(unit)));
                provisional = provisional || resolution.provisional;
                cost = {
                    category: entry.def.costCategory,
                    estimate,
                    resolved: finalValue.toNumber(),
                    source: resolution.source ?? 'ITEMS',
                    items: resolution.items,
                    provisional: resolution.provisional,
                };
            }
            else {
                // Nothing has been booked against this category, so a non-zero figure is still only expected.
                // A category the policy worked out as zero (a fee that does not apply) is not "provisional" —
                // there is nothing left to invoice.
                const stillExpected = estimate !== 0;
                cost = {
                    category: entry.def.costCategory,
                    estimate,
                    resolved: estimate,
                    source: 'EQUATION',
                    items: [],
                    provisional: stillExpected,
                };
                provisional = provisional || stillExpected;
            }
        }
        evaluated.set(id, finalValue);
        evaluatedOrder.push(id);
        outputs[id] = finalValue.toNumber();
        trace[id] = { ...baseTrace(entry), substituted, value: finalValue.toNumber(), cost };
    }
    return { outputs, trace, provisional, errors, order: evaluatedOrder };
};
/**
 * Two passes for an open estimate whose offer is not set yet (plan §5.2, §5.6): the first computes the
 * ceiling with no acquisition basis, the second re-runs with that ceiling as the basis so `total`,
 * `retail` and `profit` describe "if we bought at the recommended ceiling". Once a salesperson records
 * an offer, or once the vehicle is purchased, the basis is a real number and one pass is enough.
 */
export const runForOffer = (policy, inputs, options = {}) => {
    const first = runPolicy(policy, { ...inputs, acquisitionBasis: 0 }, options);
    const ceiling = first.outputs.ceiling;
    if (ceiling === null || ceiling === undefined) {
        return first;
    }
    return runPolicy(policy, { ...inputs, acquisitionBasis: ceiling }, options);
};
const withUpstream = (substitutions, references, evaluated) => {
    const merged = new Map(substitutions);
    for (const reference of references) {
        const value = evaluated.get(reference);
        if (value !== undefined) {
            merged.set(reference, printValue(value));
        }
    }
    return merged;
};
//# sourceMappingURL=policy-runner.js.map